import { useContext, useEffect, useState } from 'react'

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Dialog,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader
} from '@clientbase/clientbase-library'
import { Box, Stack, Tooltip, Typography, useMediaQuery } from '@mui/material'
import { DataService } from 'api/DataService'
import { useActiveMerchantForRecord } from 'lib/merchant-group'
import moment from 'moment-timezone'
import { Transfer, TransferReportProps } from 'pages/transfers/[uuid]/report'
import { ChargeContext } from 'templates/Charge/context'

import Can from 'components/Can'
import { DetailCompanyRow } from 'components/DetailCompany'
import IconContainer from 'components/IconContainer/IconContainer'
import ModalBalanceExpenseDetail from 'components/Modal/ModalBalanceExpenseDetail'
import ModalChargeDetail from 'components/Modal/ModalChargeDetail'
import { cardTittle } from 'components/Modal/ModalChargeDetail/ModalChargeDetail.utils'
import ModalInvoiceDetailsContent from 'components/Modal/ModalInvoiceDetails/ModalInvoiceDetailsContent'
import ReportTransfersTable from 'components/ReportTransfers/ReportTransfersTable.utils'
import Status from 'components/Status'

import { formatMoney } from 'utils'
import { useExportReport } from 'utils/filters/export-report'

import { theme } from 'styles/theme'

import ModalTransfersSkeleton from './ModalTransfersSkeleton'
import {
  getHoverText,
  getTransferStatusLabel
} from './ModalTransfersTable.utils'

type ModalTransfersProps = {
  onOpenExpenseDetail?: (uuid: string) => void
}

export const ModalTransfers = ({
  onOpenExpenseDetail
}: ModalTransfersProps = {}) => {
  const { modal, updateModal, updateTable } = useContext(ChargeContext)

  const isMobile = useMediaQuery('(max-width:600px)')

  // SC-10999: aberto da lista agregada do grupo, busca e age na empresa da
  // transferência (o detalhe da cobrança aberto daqui herda a mesma empresa)
  useActiveMerchantForRecord(modal.isOpenDetails ? modal.uuid : undefined)

  const [dialogIsOpen, setIsOpenDialog] = useState(false)
  const [record, setRecord] = useState<Transfer>()
  const [loadingTransfer, setLoadingTransfer] = useState(false)
  const [modalDetailInvoice, setModalDetailInvoice] = useState({
    isOpenDetailInvoice: false,
    uuid: ''
  })
  const [expenseDetailUuid, setExpenseDetailUuid] = useState('')
  const [nfseDetailUuid, setNfseDetailUuid] = useState('')

  const handleOpenExpenseDetailInternal = (uuid: string) => {
    if (onOpenExpenseDetail) {
      onOpenExpenseDetail(uuid)
    } else {
      setExpenseDetailUuid(uuid)
    }
  }

  const { handleExportReport, loading } = useExportReport({
    model: 'billing_transfer',
    filters: { transfer_uuid: modal.uuid },
    setIsOpenDialog
  })

  const handleOpenDetailInvoice = (invoiceUuid: string) => {
    setModalDetailInvoice((prevState) => ({
      ...prevState,
      isOpenDetailInvoice: true,
      uuid: invoiceUuid
    }))
  }

  const handleCloseDetails = () => {
    updateModal((prevState) => ({
      ...prevState,
      uuid: '',
      isOpenDetails: false
    }))
    setRecord(undefined)
    setLoadingTransfer(false)
  }

  const handleCloseDetailInvoice = () => {
    setModalDetailInvoice((prevState) => ({
      ...prevState,
      isOpenDetailInvoice: false,
      uuid: ''
    }))
  }

  const getTransfer = async () => {
    if (!modal.uuid) return

    try {
      setLoadingTransfer(true)
      const res = await DataService({
        type: 'GET',
        url: `/v3/transfers/${modal.uuid}`
      })

      const data: TransferReportProps = res.data
      const record = data?.record || null
      setRecord(record)
    } catch (error) {
      console.error('Erro ao carregar transferência:', error)
    } finally {
      setLoadingTransfer(false)
    }
  }

  useEffect(() => {
    if (modal.uuid && modal.isOpenDetails) {
      getTransfer()
    }
  }, [modal.uuid, modal.isOpenDetails])

  const updateDialog = (open: boolean) => {
    setIsOpenDialog(open)
  }

  return (
    <>
      <Modal
        aria-hidden={!modal.isOpenDetails}
        handleClose={handleCloseDetails}
        open={modal.isOpenDetails}
        width="100%"
        maxWidth="85vw"
        maxHeight="90vh"
      >
        {loadingTransfer || !record ? (
          <ModalTransfersSkeleton />
        ) : (
          <ModalBody>
            <ModalHeader onClose={handleCloseDetails}>
              <Stack
                direction={isMobile ? 'column' : 'row'}
                justifyContent="space-between"
                alignItems={isMobile ? 'start' : 'center'}
                width="100%"
                mr={2}
                gap={isMobile ? 1 : 0}
              >
                <Stack direction="row" alignItems="center" gap={2}>
                  <IconContainer icon="event" />
                  <Typography variant="mdBold">
                    Detalhes da transferência
                  </Typography>
                </Stack>
                <Tooltip arrow title={getHoverText(record.status).text}>
                  <span>
                    <Status
                      status={record.status}
                      label={getTransferStatusLabel(record.status)}
                    />
                  </span>
                </Tooltip>
              </Stack>
            </ModalHeader>

            <ModalContent>
              <Stack
                sx={{
                  padding: '12px 16px',
                  border: '1px solid #EDEBEB',
                  borderRadius: '5px'
                }}
                gap={1}
              >
                <DetailCompanyRow recordUuid={record.uuid} />
                <Item
                  label="Valor total"
                  value={formatMoney(Number(record?.amount))}
                />

                <Item
                  label="Data da solicitação"
                  value={moment
                    .utc(record.created_at)
                    .tz('America/Sao_Paulo')
                    .format('DD/MM/YYYY HH:mm')}
                />
                <Item
                  label="Data da transferência"
                  value={record.transfer_date
                    .slice(0, 10)
                    .split('-')
                    .reverse()
                    .join('/')}
                />
                <Item label="Detalhes" value={record.description} />

                <Typography mt={2} variant="smBold">
                  Dados Bancários
                </Typography>

                <Item
                  label="Banco"
                  value={`${record.bank_account.bank.compe}${
                    record.bank_account.agency &&
                    `- ${record.bank_account?.agency}`
                  } - ${record.bank_account.bank.short_name}`}
                />
                <Item label="Agência" value={record.bank_account.agency} />
                <Item label="Conta" value={record.bank_account.account} />
              </Stack>

              <Accordion sx={{ mt: 2 }} defaultExpanded>
                <AccordionSummary>
                  {cardTittle(
                    `Registros (${
                      record.transfers_items.length
                        ? `${record.transfers_items.length}`
                        : 'Nenhum registro encontrado'
                    })`,
                    'event',
                    theme.palette.neutral[200]
                  )}
                </AccordionSummary>
                <AccordionDetails>
                  <ReportTransfersTable
                    record={record}
                    handleOpenDetailTransfer={handleOpenDetailInvoice}
                    handleOpenExpenseDetail={handleOpenExpenseDetailInternal}
                  />
                </AccordionDetails>
              </Accordion>

              <ModalChargeDetail
                handleCloseChargeDetail={handleCloseDetailInvoice}
                isOpenChargeDetail={modalDetailInvoice.isOpenDetailInvoice}
                uuid={modalDetailInvoice.uuid}
                zIndex={2200}
              />
            </ModalContent>

            <ModalFooter>
              <Can permission="reports:export">
                <Tooltip
                  arrow
                  placement="left"
                  title="Seu relatório será exportado integralmente a não ser que você selecione um período ou filtro ao lado."
                >
                  <Button
                    variantButton="outlined"
                    aria-labelledby="Exportar Relatório"
                    disabled={loading}
                    onClick={() => updateDialog(true)}
                  >
                    Exportar Relatório
                  </Button>
                </Tooltip>
              </Can>
            </ModalFooter>
          </ModalBody>
        )}
      </Modal>

      <Dialog
        icon="description"
        title="Relatório"
        description="Confirma exportar o relatório?"
        setIsOpenDialog={() => updateDialog(false)}
        isOpenDialog={dialogIsOpen}
        cancelButton
      >
        <Button autoFocus onClick={handleExportReport} fullWidth>
          Exportar
        </Button>
      </Dialog>

      {!onOpenExpenseDetail && (
        <>
          <ModalBalanceExpenseDetail
            isOpen={!!expenseDetailUuid}
            uuid={expenseDetailUuid}
            hideTransferRow
            handleClose={() => setExpenseDetailUuid('')}
            onOpenBillingDetail={(uuid) => {
              handleOpenDetailInvoice(uuid)
            }}
            onOpenNfseDetail={(uuid) => {
              setNfseDetailUuid(uuid)
            }}
            onOpenTransferDetail={(uuid) => {
              setExpenseDetailUuid('')
              updateModal((prev) => ({ ...prev, uuid, isOpenDetails: true }))
            }}
          />

          <Modal
            open={!!nfseDetailUuid}
            handleClose={() => setNfseDetailUuid('')}
            width="100%"
            maxWidth="85vw"
            maxHeight="90vh"
          >
            <ModalInvoiceDetailsContent
              uuid={nfseDetailUuid}
              handleReloadTable={updateTable}
              handleCloseModal={() => setNfseDetailUuid('')}
            />
          </Modal>
        </>
      )}
    </>
  )
}

const Item = ({ label, value }: { label: string; value: any }) => {
  return (
    <Stack direction="row" alignItems="flex-end" gap={1}>
      <Typography color="#777777">{label}</Typography>
      <Box
        sx={{
          width: '1fr',
          flexGrow: '1',
          borderBottom: '0.5px dashed #CBCBCB',
          mb: '4px'
        }}
      />
      <Typography variant="smBold">{value}</Typography>
    </Stack>
  )
}
